import * as echarts from 'echarts/core';
import { UTCDate } from '@date-fns/utc';
import { startOfMonth } from 'date-fns';
import { sortBy, sum } from 'lodash';
import { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, MarkAreaComponent, DataZoomComponent, LegendScrollComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import Empty from 'antd/lib/empty';

import { IVendorBillItem } from '../../../domain/vendor-bill-item/interface';
import { getUtcFormattedTime } from '../../../domain/date/date';
import { PRIMARY_COLOR } from '../../../theme';
import { numberFormatter } from '../../../domain/common/numberFormatter';

echarts.use([BarChart, TooltipComponent, GridComponent, CanvasRenderer, MarkAreaComponent, DataZoomComponent, LegendScrollComponent]);

type PaymentByMonthMapItem = {
    capacityPayment: number[];
    energyPayment: number[];
};

type PaymentsDataItem = {
    timestamp: number;
    capacityPayment: number;
    energyPayment: number;
    total: number;
};

const prepareData = (data: IVendorBillItem[]) => {
    const monthMap = new Map<number, PaymentByMonthMapItem>();

    const emptyRecord: PaymentByMonthMapItem = {
        capacityPayment: [],
        energyPayment: [],
    };

    const sortedData = sortBy(data, 'period');

    sortedData.forEach(val => {
        const { period, capacity_payment = 0, energy_payment = 0 } = val;

        if (!period) return;

        const monthKey = startOfMonth(new UTCDate(period)).valueOf();

        if (!monthMap.has(monthKey)) {
            monthMap.set(monthKey, emptyRecord);
        }

        const item = structuredClone(monthMap.get(monthKey)!);

        item.capacityPayment.push(capacity_payment);
        item.energyPayment.push(energy_payment);

        monthMap.set(monthKey, item);
    });

    const result: PaymentsDataItem[] = [];

    if (!monthMap.size) return [];

    monthMap.forEach((item, timestamp) => {
        const capacityPaymentSum = Math.round(sum(item.capacityPayment));
        const energyPaymentSum = Math.round(sum(item.energyPayment));

        result.push({
            timestamp,
            capacityPayment: capacityPaymentSum,
            energyPayment: energyPaymentSum,
            total: capacityPaymentSum + energyPaymentSum,
        });
    });

    return result;
};

export const PaymentsBarChart = ({ data }: { data: IVendorBillItem[] }) => {
    const preparedData: PaymentsDataItem[] = useMemo(() => prepareData(data), [data]);
    const isDataExist = data.length;

    const option = {
        legend: {
            data: ['Capacity Payment', 'Energy Payment'],
            selectedMode: false,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                label: {
                    show: false,
                },
            },
            formatter: (params: any) => {
                if (!params.length) return '';

                const timestamp = Number(params.at(0)?.name);
                const date = new Date(timestamp);
                const formattedDate = getUtcFormattedTime(date, 'MMM/yyyy');

                const lines = [];

                lines.push(`<div>${formattedDate}</div>`);

                params.forEach((item: any) => {
                    lines.push(
                        `
                            <div>
                                <span style='display: inline-block; margin-right: 6px; width: 10px; height: 10px; background: ${item.color}; border-radius: 50%;'></span>
                                ${item.seriesName}: $ ${numberFormatter.format(item.value.at(1)) ?? 0}
                            </div>
                        `
                    );
                });

                return lines.join('');
            },
        },
        grid: {
            right: '5.5%',
        },
        xAxis: {
            type: 'category',
            axisLabel: {
                color: '#697475',
                margin: 10,
                formatter: (value: number) => {
                    const timestamp = Number(value);
                    const date = new Date(timestamp);

                    return getUtcFormattedTime(date, 'MMM/yyyy');
                },
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#d6dbdb',
                    width: 2,
                    type: 'solid',
                },
            },
            axisTick: { show: true },
        },
        yAxis: {
            type: 'value',
            splitLine: {
                show: true,
            },
            axisLabel: {
                formatter: (value: number) => `$ ${numberFormatter.format(value)}`,
            },
        },
        series: [
            {
                name: 'Capacity Payment',
                type: 'bar',
                stack: 'payments',
                data: preparedData.map(item => [item.timestamp, item.capacityPayment]),
                barWidth: 50,
                itemStyle: {
                    color: PRIMARY_COLOR,
                },
            },
            {
                name: 'Energy Payment',
                type: 'bar',
                stack: 'payments',
                data: preparedData.map(item => [item.timestamp, item.energyPayment]),
                barWidth: 50,
                itemStyle: {
                    color: '#4a7088',
                },
            },
            {
                name: 'Total',
                type: 'line',
                data: preparedData.map(item => [item.timestamp, item.total]),
                symbolSize: 0,
                emphasis: {
                    scale: false,
                },
                lineStyle: {
                    width: 0,
                },
                itemStyle: {
                    color: 'rgba(187, 195, 195, 0.6)',
                },
                label: {
                    show: true,
                    position: 'top',
                    formatter: (params: any) => {
                        return `$ ${numberFormatter.format(Number(params.data.at(1)))}`;
                    },
                    color: '#333',
                    backgroundColor: '#fff',
                    fontSize: 14,
                },
            },
        ],
    };

    return isDataExist ? (
        <ReactEChartsCore echarts={echarts} option={option} notMerge={true} lazyUpdate={true} />
    ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );
};
